import React, { useEffect, useState, useMemo } from "react";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import MuiCheckbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from '@mui/material/Grid';

import { getAllAdmins, getCurrentOrg, dreamUsers, dreamUserWithRoles, api2AdminRoles, api2AdminSetRole } from "../../utils/operations.js";
import { observer, inject } from "mobx-react";
import API from '../../utils/api';
import axios from "axios";
import { toast } from 'react-toastify';
import { api2AdminAdditionalOrganizations } from '../../utils/operations';
import { getUserRolesObject, spatieRoleNameToDreamId } from "../../utils/misc";

const checkEmail = email => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

const NewAdmin = ({ Auth, history }) => {
  const [data, setData] = useState({
    name: "",
    organisation_id: "",
    email: "",
    password: "",
    show_finance: true,
    edit_finance: false,
    show_charts: false,
    show_future_invoices: false,
  });
  const [roleId, setRoleId] = useState('');
  const [roles, setRoles] = useState([]);
  const [adminOrganisations, setAdminOrganisations] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [textErrors, setTextErrors] = useState([]);

  const isPossibleAdditionalSchools = useMemo(
    () => ['admin', 'superadmin'].includes(roles.find(r => r.id === roleId)?.name),
    [roles, roleId]
  );

  useEffect(() => {
    if (!Auth.can('administrators')) { history.push("/children"); return; }
    axios.all([
      API.get(getCurrentOrg),
      API.get(api2AdminRoles),
    ]).then(axios.spread((orgs, rolesRes) => {
      setOrganisations((orgs.data.resource || []).filter(Boolean));
      const loadedRoles = (rolesRes.data.roles || []).filter(Boolean);
      setRoles(loadedRoles);
      const defaultRole = loadedRoles.find(r => r.name === 'manager') || loadedRoles[0];
      if (defaultRole) setRoleId(defaultRole.id);
    })).catch(() => {
      API.get(getCurrentOrg).then(res =>
        setOrganisations((res.data.resource || []).filter(Boolean))
      );
    });
  }, []);

  const handleChange = ({ target: { id, value } }) => setData(prev => ({ ...prev, [id]: value }));

  const handleRoleChange = e => setRoleId(e.target.value);

  const handlePermissionsToggle = name => {
    if (Auth.role !== 'superadmin') return;
    setData(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const checkInfo = () => {
    const errors = [];
    if (!data.name) errors.push(<span key="name">* Name cannot be empty</span>);
    if (!data.organisation_id) errors.push(<span key="org">* Organisation cannot be empty</span>);
    if (!checkEmail(data.email)) errors.push(<span key="email">* Email must be valid</span>);
    if (data.password.length < 6) errors.push(<span key="pw">* Password length must be at least 6 symbols</span>);
    if (errors.length > 0) { setTextErrors(errors); return false; }
    return true;
  };

  const saveData = async () => {
    try {
      const selectedRoleName = roles.find(r => r.id === roleId)?.name || 'admin';
      const dreamRoleId = spatieRoleNameToDreamId(selectedRoleName);
      const newUser = {
        resource: [{
          name: data.name, first_name: data.name, last_name: data.name,
          is_active: true, email: data.email, password: data.password, phone: selectedRoleName
        }]
      };
      const createdUser = await API.post(dreamUsers, newUser);
      const admin_id = createdUser.data.resource[0].id;

      await API.put(dreamUserWithRoles(admin_id), getUserRolesObject(admin_id, dreamRoleId));
      await API.post(getAllAdmins, {
        resource: [{
          id: admin_id, name: data.name, organisation_id: data.organisation_id,
          email: data.email, password: data.password,
          show_finance: data.show_finance, edit_finance: data.edit_finance,
          show_charts: data.show_charts, show_future_invoices: data.show_future_invoices,
        }]
      });
      await API.post(api2AdminSetRole(admin_id), { role_id: roleId });
      const extra = adminOrganisations.filter(id => id !== data.organisation_id);
      await API.post(api2AdminAdditionalOrganizations(admin_id), extra);

      toast.success('Administrator successfully added!');
      setTimeout(() => history.push("/admins"), 1000);
    } catch {
      toast.error('Something went wrong!');
    }
  };

  const saveInfo = () => {
    if (checkInfo()) saveData();
    else setOpenDialog(true);
  };

  return (
    <div style={{ maxWidth: 500, margin: "auto" }}>
      <br />
      <TextField id="name" label="Name" fullWidth value={data.name} onChange={handleChange} sx={{ mb: 2 }} />
      {Auth.role === 'superadmin' && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="role-label">Role</InputLabel>
          <Select labelId="role-label" label="Role" value={roleId} onChange={handleRoleChange}>
            {roles.map(r => r && <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
          </Select>
        </FormControl>
      )}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="org-label">School</InputLabel>
        <Select labelId="org-label" label="School" value={data.organisation_id} onChange={({ target: { value } }) => setData(prev => ({ ...prev, organisation_id: value }))}>
          {organisations.map(row => row && <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>)}
        </Select>
      </FormControl>
      <TextField id="email" label="Email" fullWidth value={data.email} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField id="password" label="Password" fullWidth value={data.password} onChange={handleChange} sx={{ mb: 2 }} />
      {roles.find(r => r.id === roleId)?.name !== 'superadmin' && (
        <Grid container spacing={3} style={{ marginTop: '20px' }}>
          <Grid size={6}>
            <FormControlLabel control={<MuiCheckbox checked={data.show_finance} onChange={() => handlePermissionsToggle('show_finance')} />} label="Show Finance Info" />
            <FormControlLabel control={<MuiCheckbox checked={data.edit_finance} onChange={() => handlePermissionsToggle('edit_finance')} />} label="Edit Finance" />
          </Grid>
          <Grid size={6}>
            <FormControlLabel control={<MuiCheckbox checked={data.show_future_invoices} onChange={() => handlePermissionsToggle('show_future_invoices')} />} label="View Future Invoices" />
            <FormControlLabel control={<MuiCheckbox checked={data.show_charts} onChange={() => handlePermissionsToggle('show_charts')} />} label="View Financial Charts" />
          </Grid>
        </Grid>
      )}
      {isPossibleAdditionalSchools && (
        <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
          <InputLabel id="add-orgs-label">Additional schools</InputLabel>
          <Select
            labelId="add-orgs-label" label="Additional schools" multiple
            value={adminOrganisations}
            onChange={e => setAdminOrganisations(e.target.value)}
          >
            {organisations.filter(o => o && o.id !== data.organisation_id).map(row => (
              <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <div style={{ display: 'flex', gap: 10, paddingTop: 20, paddingBottom: 20 }}>
        <Button variant="contained" color="primary" onClick={saveInfo}>Save</Button>
        <Button variant="contained" color="secondary" onClick={() => history.push("/admins")}>Cancel</Button>
      </div>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Form errors:</DialogTitle>
        <DialogContent><div>{textErrors}</div></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default inject("Auth")(observer(NewAdmin));
